import React, {useEffect, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {FaExternalLinkAlt, FaEye, FaSyncAlt} from "react-icons/fa";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import Chat from "../../../../components/chat/chat.jsx";
import ChatHistory from "../../../../components/chat/chat-history.jsx";
import Loader from "../../../../components/template/loader.jsx";
import StatusCell from "../../utils/components/status-cell.jsx";
import AttendanceService from "../../../../services/dispatch/attendance-service.js";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import TaskService from "../../../../services/dispatch/task-service.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import DashboardInfoTable, {
    DashboardInfoTableCell as Cell,
    DashboardInfoTableHead as Head,
    DashboardInfoTableTitle as SectionTitle,
} from "./dashboard-info-table.jsx";
import projectUtils
    from "../../../../utils/project-utils.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import AttachShow from "../../../../components/attach/attach-show.jsx";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;
const API_URL = import.meta.env.VITE_API_URL ?? "";

const hasValue = value => value !== null && value !== undefined && value !== "";
const asArray = value => Array.isArray(value) ? value : [];
const text = value => hasValue(value) ? value : "";
const getTaskSourceEditUrl = task => {
    if (!hasValue(task?.sourceId)) return null;
    if (task.source === "ATTENDANCE") return `${DESPACHO_WEB_URL}/attendance/form/${task.sourceId}?redir=despacho/dashboard`;
    if (task.source === "SOS_SCHEDULED_VISIT") return `/sos/citizen-scheduled-visit?schedule_id=${task.sourceId}&redir=despacho/dashboard`;
    if (task.source === "JANITORIAL") return `/janitorial/form/${task.sourceId}?redir=despacho/dashboard`;
    return null;
};

const DispatchRows = ({dispatches, attendance}) => asArray(dispatches).map(dispatch => {
    const supports = asArray(dispatch.supports);

    return (
        <React.Fragment key={`dispatch-${dispatch.id}`}>
            <tr>
                <Cell><StatusCell active={!!dispatch.status}/></Cell>
                <Cell>{text(dispatch.compositionName)}</Cell>
                <Cell>{text(dispatch.displayContingent)}</Cell>
                <Cell>{hasValue(dispatch.nature) ? dispatch.nature : attendance.nature}</Cell>
                <Cell>{dispatch.numberCheckbook || "Sem talão"}</Cell>
                <Cell>{text(dispatch.occurrenceNumber)}</Cell>
            </tr>
            {supports.length > 0 && (
                <tr>
                    <Cell colSpan={6}>
                        <DashboardInfoTable>
                            <tbody>
                                {supports.map(support => (
                                    <tr key={`support-${dispatch.id}-${support.id ?? support.garrisonId}`}>
                                        <Cell className={"text-[12px]!"}><StatusCell active={!!support.status}/></Cell>
                                        <Cell className={"text-[12px]!"}>{text(support.compositionName ?? support.garrisonName)}</Cell>
                                        <Cell className={"text-[12px]!"}>{text(support.displayContingent)}</Cell>
                                        <Cell className={"text-[12px]!"}>{support.numberCheckbook || "Sem talão"}</Cell>
                                    </tr>
                                ))}
                            </tbody>
                        </DashboardInfoTable>
                    </Cell>
                </tr>
            )}
        </React.Fragment>
    );
});

const DispatchTable = ({dispatches, boName, attendance}) => {
    if (!asArray(dispatches).length) return null;

    return (
        <DashboardInfoTable className="mb-3">
            <thead>
                <SectionTitle colSpan={6}>Despachos</SectionTitle>
                <tr>
                    <Head>Status</Head>
                    <Head>Guarnição</Head>
                    <Head>Contingente</Head>
                    <Head>Natureza</Head>
                    <Head>Nº talão</Head>
                    <Head>Nº {boName}</Head>
                </tr>
            </thead>
            <tbody>
                <DispatchRows dispatches={dispatches} attendance={attendance}/>
            </tbody>
        </DashboardInfoTable>
    );
};

const GarrisonNearbyTable = ({attendance, dispatches, permissions}) => {
    const [enabled, setEnabled] = useState(false);
    const {openNearbyGarrisonDispatch} = useDashboardContext();
    const attendanceId = attendance.id ?? attendance.attendanceId;
    const hasDispatchesActive = asArray(dispatches).some(dispatch => dispatch.status);
    const actionText = hasDispatchesActive ? "Gerar apoio" : "Despachar";
    const canShow = hasValue(attendance.factLatitude) && permissions?.dispatch;
    const formatIntegrants = integrants => Array.isArray(integrants)
        ? integrants
            .map(integrant => typeof integrant === "object"
                ? integrant?.namewar ?? integrant?.nameWar ?? integrant?.name
                : integrant)
            .filter(hasValue)
            .join(", ")
        : text(integrants);

    const nearbyQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.garrisonNearby(attendanceId),
        queryFn: () => DispatchService.getGarrisonNearby(attendanceId),
        enabled: enabled && canShow && hasValue(attendanceId),
    });

    if (!canShow) return null;

    const listGarrisons = () => {
        if (enabled) {
            nearbyQuery.refetch();
            return;
        }

        setEnabled(true);
    };

    const handleAction = item => {
        openNearbyGarrisonDispatch?.({
            attendance: {
                ...attendance,
                attendanceId,
                nature: attendance.nature,
                natureText: attendance.natureText ?? attendance.nature,
                place: attendance.place ?? [attendance.factPlace, attendance.factStreet, attendance.factNumber].filter(Boolean).join(" - "),
            },
            garrison: {
                ...item,
                garrisonId: item.garrisonId,
                garrisonName: item.name,
            },
        });
    };

    return (
        <DashboardInfoTable className="mb-3">
            <thead>
                <SectionTitle colSpan={6}>
                    <div className="relative">
                        <span>Guarnições disponíveis ou em atividade próximas ao atendimento</span>
                        <Button className={"px-2.5! h-5.25! absolute top-0 right-0"} target="default" onClick={listGarrisons} loading={nearbyQuery.isFetching} loadingText="Listando...">
                            <FaSyncAlt/> Listar
                        </Button>
                    </div>
                </SectionTitle>
                <tr>
                    <Head>Guarnição</Head>
                    <Head>Contingentes</Head>
                    <Head>Natureza</Head>
                    <Head>Distância</Head>
                    <Head>Tempo de deslocamento</Head>
                    <Head></Head>
                </tr>
            </thead>
            <tbody>
                {nearbyQuery.isError && (
                    <tr>
                        <Cell colSpan={6}>Não foi possível carregar as guarnições próximas.</Cell>
                    </tr>
                )}
                {enabled && nearbyQuery.isSuccess && !asArray(nearbyQuery.data).length && (
                    <tr>
                        <Cell colSpan={6}>Nenhuma guarnição próxima encontrada.</Cell>
                    </tr>
                )}
                {asArray(nearbyQuery.data).map(item => (
                    <tr key={item.garrisonId}>
                        <Cell>{text(item.name)}</Cell>
                        <Cell>{formatIntegrants(item.integrants)}</Cell>
                        <Cell>{text(item.dispatchNature)}</Cell>
                        <Cell>{text(item.distance)}</Cell>
                        <Cell>{text(item.distanceTime)}</Cell>
                        <Cell>
                            <Button className={"h-5.25!"} onClick={() => handleAction(item)}>
                                {actionText}
                            </Button>
                        </Cell>
                    </tr>
                ))}
            </tbody>
        </DashboardInfoTable>
    );
};

const AssignmentTable = ({assignments}) => {
    if (!asArray(assignments).length) return null;

    const navigate = navigateHook();
    const openGarrison = (garrisonId) => {
        const url = `${DESPACHO_WEB_URL}/garrison/form/${garrisonId}?redir=despacho/dashboard`
        navigate(url, true);
    }

    return (
        <DashboardInfoTable className="mb-3">
            <thead>
                <SectionTitle colSpan={1}>Atribuições</SectionTitle>
                <tr>
                    <Head>Guarnição</Head>
                </tr>
            </thead>
            <tbody>
                {assignments.map(item => (
                    <tr key={item.id}>
                        <Cell>
                            <p className="cursor-pointer text-[#428bca] hover:underline" onClick={() => openGarrison(item.garrisonId)}>
                                {text(item.garrisonName)}
                            </p>
                        </Cell>
                    </tr>
                ))}
            </tbody>
        </DashboardInfoTable>
    );
};

const AttachmentTable = ({attachments}) => {
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    if (!asArray(attachments).length) return null;

    return (
        <>
            <DashboardInfoTable className="mb-3">
                <thead>
                    <SectionTitle colSpan={1}>Anexos</SectionTitle>
                </thead>
                <tbody>
                    {attachments.map(attachment => {
                        const canPreview = String(attachment.type ?? "").includes("image") || String(attachment.type ?? "").includes("application/pdf");
                        const attachId = attachment.reference ?? attachment.referenceId ?? attachment.attachId ?? attachment.id;

                        return (
                            <tr key={attachment.id ?? attachId}>
                                <Cell>
                                    {canPreview && attachId && (
                                        <button
                                            type="button"
                                            className="mr-2 inline-flex cursor-pointer text-[#525252]"
                                            title="Visualizar anexo"
                                            onClick={() => setSelectedAttachment({attachId, name: attachment.name})}
                                        >
                                            <FaEye/>
                                        </button>
                                    )}
                                    {text(attachment.name)}
                                </Cell>
                            </tr>
                        );
                    })}
                </tbody>
            </DashboardInfoTable>

            <AttachShow
                open={!!selectedAttachment}
                setOpen={open => !open && setSelectedAttachment(null)}
                attachId={selectedAttachment?.attachId}
                showRemove={false}
                modalTitle={selectedAttachment?.name ?? "Anexo"}
                animated={false}
            />
        </>
    );
};

const AttendanceDetails = ({data, permissions, sourceAttendance}) => {
    const navigate = navigateHook();
    const attendance = {...(sourceAttendance ?? {}), ...(data.attendance ?? {})};
    const sectors = attendance.sectors && attendance.sectors !== -1 ? attendance.sectors : "";

    const openSos = () => {
        navigate(`/sos/event-open/${attendance.sosId}`, true);
    }

    const openNotification = () => {
        const id = attendance.notificationId
        AttendanceService.isNotificationSigned(id)
            .then((signed) => {
                const target = signed ? "notifications-signed" : "notifications-open";
                navigate(`/${target}?id=${id}`, true);
            })
            .catch((error) => {
                console.error(error);
                const message = error.response?.data ?? "Não foi possível abrir a notificação.";
                projectUtils.swalErrorMessage("", message, "Ok");
            });
    }

    return (
        <>
            {attendance.ramalIdentify && (
                <div className="mb-2 border border-[#faebcc] bg-[#fcf8e3] px-3 py-2 text-center text-[13px] text-[#8a6d3b]">
                    Atendimento criado automaticamente por {attendance.ramalIdentify}
                </div>
            )}

            <DashboardInfoTable className="mb-2">
                <tbody>
                    <SectionTitle colSpan={5}>Dados iniciais</SectionTitle>
                    <tr>
                        <Head>Protocolo</Head>
                        <Head>Status</Head>
                        <Head>Prioridade</Head>
                        <Head>Cadastro</Head>
                        <Head>Última alteração</Head>
                    </tr>
                    <tr>
                        <Cell>{text(attendance.protocol)}</Cell>
                        <Cell>{text(attendance.statusName)}</Cell>
                        <Cell>{text(attendance.priority)}</Cell>
                        <Cell>{text(attendance.userCreated)}</Cell>
                        <Cell>{text(attendance.lastUser)}</Cell>
                    </tr>
                    <tr>
                        <Head colSpan={3}>Natureza</Head>
                        <Head colSpan={2}>Origem</Head>
                    </tr>
                    <tr>
                        <Cell colSpan={3}>{text(attendance.nature)}</Cell>
                        <Cell colSpan={2}>{text(attendance.source)}</Cell>
                    </tr>
                    <tr>
                        <Head colSpan={2}>Canal</Head>
                        <Head>Solicitante</Head>
                        <Head>Telefone</Head>
                        <Head>CPF</Head>
                    </tr>
                    <tr>
                        <Cell colSpan={2}>{text(attendance.channel)}</Cell>
                        <Cell>{text(attendance.contactName)}</Cell>
                        <Cell>{projectUtils.formatPhone(attendance.contactPhone)}</Cell>
                        <Cell>{projectUtils.formatCpfToView(attendance.contactCpf)}</Cell>
                    </tr>
                    {attendance.transcription && (
                        <>
                            <tr>
                                <Head colSpan={5}>
                                    Transcrição
                                    {attendance.sosId && (
                                        <p className="cursor-pointer ml-2 inline-flex items-center gap-1 text-[#383838] text-[12px] hover:underline" onClick={openSos}>
                                            (Mais informações <FaExternalLinkAlt/>)
                                        </p>
                                    )}
                                    {attendance.notificationId && (
                                        <span className="cursor-pointer ml-2 inline-flex items-center gap-1 text-[#383838] text-[12px] hover:underline" onClick={openNotification}>
                                            (Mais informações <FaExternalLinkAlt/>)
                                        </span>
                                    )}
                                </Head>
                            </tr>
                            <tr><Cell colSpan={5}>{attendance.transcription}</Cell></tr>
                        </>
                    )}
                    {attendance.callFile && (
                        <>
                            <tr><Head colSpan={5}>Gravação de chamada telefônica</Head></tr>
                            <tr>
                                <Cell colSpan={5}>
                                    <audio className="mt-2 w-full" controls controlsList="nodownload" preload="auto" src={AttendanceService.attachmentUrl(attendance.callFile.id)}/>
                                </Cell>
                            </tr>
                        </>
                    )}
                    {attendance.observation && (
                        <>
                            <tr><Head colSpan={5}>Observação do atendente</Head></tr>
                            <tr><Cell colSpan={5}>{attendance.observation}</Cell></tr>
                        </>
                    )}
                </tbody>
            </DashboardInfoTable>

            <DashboardInfoTable className="mb-2">
                <tbody>
                    <SectionTitle colSpan={4}>Endereço do fato</SectionTitle>
                    <tr>
                        <Head>Tipo</Head>
                        <Head>Local pré-cadastrado</Head>
                        <Head>Logradouro</Head>
                        <Head>Número</Head>
                    </tr>
                    <tr>
                        <Cell>{text(attendance.factType)}</Cell>
                        <Cell>{text(attendance.factPlace)}</Cell>
                        <Cell>{text(attendance.factStreet)}</Cell>
                        <Cell>{text(attendance.factNumber)}</Cell>
                    </tr>
                    <tr>
                        <Head>Bairro</Head>
                        <Head>Cruzamento</Head>
                        <Head>Ponto de referência</Head>
                        <Head>Município</Head>
                    </tr>
                    <tr>
                        <Cell>{text(attendance.factNeighborhood)}</Cell>
                        <Cell>{text(attendance.factCrossroads)}</Cell>
                        <Cell>{text(attendance.factReferencePoint)}</Cell>
                        <Cell>{text(attendance.factCity)}</Cell>
                    </tr>
                    {sectors && (
                        <tr>
                            <Head>Setores</Head>
                            <Cell colSpan={3}>{sectors}</Cell>
                        </tr>
                    )}
                </tbody>
            </DashboardInfoTable>

            <DispatchTable dispatches={data.dispatches} boName={data.boName} attendance={attendance}/>
            <GarrisonNearbyTable attendance={attendance} dispatches={data.dispatches} permissions={permissions}/>
            <AttachmentTable attachments={attendance.attachment}/>
            <AssignmentTable assignments={data.assignment}/>
        </>
    );
};

const TaskDetails = ({data, sourceEditUrl}) => {
    const navigate = navigateHook();
    const task = data.task ?? {};

    const openSource = () => {
        navigate(sourceEditUrl, true);
    }

    const hasSource = Boolean(sourceEditUrl);

    return (
        <>
            <DashboardInfoTable className="mb-2">
                <tbody>
                    <SectionTitle colSpan={6}>Dados iniciais</SectionTitle>
                    <tr><Head colSpan={6}>Natureza</Head></tr>
                    <tr><Cell colSpan={6}>{text(task.reasonText)}</Cell></tr>
                    {task.description && (
                        <>
                            <tr><Head colSpan={6}>Descrição</Head></tr>
                            <tr>
                                <Cell colSpan={6}>
                                    {task.description}
                                    {hasSource && (
                                        <p className="cursor-pointer ml-2 inline-flex text-[#428bca]" onClick={openSource}>
                                            <FaExternalLinkAlt/>
                                        </p>
                                    )}
                                </Cell>
                            </tr>
                        </>
                    )}
                </tbody>
            </DashboardInfoTable>

            <DashboardInfoTable className="mb-2">
                <tbody>
                    <SectionTitle colSpan={4}>Endereço do fato</SectionTitle>
                    <tr>
                        <Head>Local pré-cadastrado</Head>
                        <Head>Bairro</Head>
                        <Head>Logradouro</Head>
                        <Head>Número</Head>
                    </tr>
                    <tr>
                        <Cell>{text(task.place)}</Cell>
                        <Cell>{text(task.neighborhood)}</Cell>
                        <Cell>{text(task.street)}</Cell>
                        <Cell>{text(task.number)}</Cell>
                    </tr>
                </tbody>
            </DashboardInfoTable>

            <DispatchTable dispatches={data.dispatches} boName={data.boName}/>
            <AssignmentTable assignments={data.assignment}/>
        </>
    );
};

const AttendanceChatPanel = ({attendance}) => {
    const chatRef = useRef(null);
    if (!attendance.hasChat) return null;

    return (
        <div className="flex w-full shrink-0 flex-col bg-white lg:w-[380px]">
            {attendance.chatFinalized ? (
                <ChatHistory
                    url={AttendanceService.getChat(attendance.id ?? attendance.attendanceId)}
                    title="Chat do atendimento"
                    height="h-[620px] lg:h-full"
                />
            ) : (
                <Chat
                    ref={chatRef}
                    url={AttendanceService.getUserChatAuth(attendance.id ?? attendance.attendanceId)}
                    title={`${attendance.contactName ?? ""}${attendance.contactPhone ? ` - ${attendance.contactPhone}` : ""}`}
                    height="h-[620px] lg:h-full"
                    onSendMessage={() => {}}
                />
            )}
        </div>
    );
};

const DashboardCardInformationModal = ({payload, boName, onClose}) => {
    const requested = Boolean(payload);
    const type = payload?.type;
    const item = payload?.item ?? {};
    const itemId = type === "task" ? item.id : item.attendanceId;
    const {permissions, navigate} = useDashboardContext();
    const title = type === "task" ? "Informações da tarefa" : "Informações do atendimento";

    const detailsQuery = useQuery({
        queryKey: type === "task"
            ? DASHBOARD_QUERY_KEYS.taskDetails(itemId)
            : DASHBOARD_QUERY_KEYS.attendanceDetails(itemId),
        queryFn: () => type === "task" ? TaskService.taskByIdToView(itemId) : AttendanceService.attendanceByIdToView(itemId),
        enabled: requested && hasValue(itemId),
    });
    const isReady = !detailsQuery.isFetching && (detailsQuery.isSuccess || detailsQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    const data = {
        ...(detailsQuery.data ?? {}),
        boName,
    };
    const attendance = {...item, ...(data.attendance ?? {})};
    const taskSourceEditUrl = getTaskSourceEditUrl(data.task);
    const showChat = type === "attendance" && attendance.hasChat;
    const canEdit = Boolean(
        (type === "attendance" && permissions.attendance)
        || (type === "task" && permissions.task && taskSourceEditUrl)
    );

    const edit = () => {
        const url = type === "task"
            ? taskSourceEditUrl
            : `${DESPACHO_WEB_URL}/attendance/form/${itemId}?redir=despacho/dashboard`;
        navigate(url, true);
    }

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title={title} className={showChat ? "w-10/12" : "w-full max-w-7xl"} animated={false}>
            <Modal.Body className="max-h-[calc(100vh-176px)] overflow-auto">
                {detailsQuery.isError && (
                    <div className="m-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        Não foi possível carregar as informações.
                    </div>
                )}
                {detailsQuery.isSuccess && (
                    <div className={`gap-2 flex flex-col lg:flex-row ${showChat ? "lg:h-[calc(100vh-200px)]" : ""}`}>
                        <div className={`min-w-0 flex-1 ${showChat ? "lg:overflow-auto" : ""}`}>
                            {type === "attendance" && (
                                <AttendanceDetails data={data} permissions={permissions} sourceAttendance={item}/>
                            )}
                            {type === "task" && <TaskDetails data={data} sourceEditUrl={taskSourceEditUrl}/>}
                        </div>
                        <AttendanceChatPanel attendance={attendance}/>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {canEdit && (
                    <Button type="button" onClick={edit} disabled={detailsQuery.isLoading}>Editar</Button>
                )}
                <Button type="button" target="primary" onClick={closeModal}>Ok</Button>
            </Modal.Footer>
            </Modal>
        </>
    );
};

export default DashboardCardInformationModal;
